/**
 * Client du relais de calcul d'itinéraire.
 *
 * Le navigateur ne peut pas appeler OSRM directement — aucun en-tête CORS
 * exploitable — d'où le relais `api/route.php`, qui met aussi le résultat en
 * cache pour tous les visiteurs.
 *
 * Deux services, deux sémantiques :
 *
 *  - **`route`** suit l'ordre fourni. `continue_straight=false` y est imposé
 *    côté serveur : le profil voiture interdit sinon le demi-tour à chaque
 *    point intermédiaire et fabrique des boucles aberrantes autour d'un point
 *    posé au doigt sur la carte.
 *  - **`trip`** signifie « passe par ces points » : le serveur les ordonne
 *    lui-même sur le réseau routier réel, ce qu'un tri à vol d'oiseau ne sait
 *    pas faire. Réservé aux détours, à partir de deux points de passage.
 */

import { decodePolyline6Validated } from './polyline'
import type { PolylinePoint } from './polyline'
import type { RoutingProfile } from './types'

/** Chemin relatif : l'application peut vivre dans un sous-répertoire. */
const ROUTE_ENDPOINT = './api/route.php'

/** Même plafond que le relais, vérifié ici pour éviter un aller-retour. */
const MAX_WAYPOINTS = 100

/** Nom de profil attendu par le relais : `car`, `bike` ou `foot`. */
export function apiProfileName(profile: RoutingProfile): string {
  return profile.id.toLowerCase()
}

export interface RouteWaypoint {
  readonly lat: number
  readonly lon: number
}

export interface RouteResult {
  readonly points: readonly PolylinePoint[]
  /** Longueur de l'itinéraire en mètres, telle qu'OSRM la calcule. */
  readonly distance: number
  readonly duration: number
}

export type RoutingService = 'route' | 'trip'

export interface RouteRequest {
  readonly waypoints: readonly RouteWaypoint[]
  /** Nom accepté par le relais — voir `apiProfileName`. */
  readonly profile: string
  readonly service?: RoutingService
  readonly signal?: AbortSignal
}

/**
 * Demande un itinéraire au relais.
 *
 * @returns `null` sur tout échec — service indisponible, points non
 *   raccordables au réseau, réponse invalide. L'appelant décide seul s'il faut
 *   le signaler : un échec au démarrage doit rester silencieux, un échec
 *   déclenché par l'utilisateur non.
 * @throws Si la requête est annulée par son `AbortSignal`, pour distinguer
 *   l'abandon volontaire de l'échec.
 */
export async function requestRoute(
  request: RouteRequest,
): Promise<RouteResult | null> {
  const { waypoints, profile, service = 'route', signal } = request

  if (waypoints.length < 2 || waypoints.length > MAX_WAYPOINTS) return null

  const coords = waypoints
    .map((point) => `${point.lat.toFixed(6)},${point.lon.toFixed(6)}`)
    .join(';')

  // En `POST` : le serveur journalise l'URL avec l'adresse IP du visiteur, et
  // des coordonnées en paramètre de requête y resteraient pour toute la durée
  // de rétention de l'hébergeur. Le corps, lui, n'est pas journalisé.
  const form = new URLSearchParams({ coords, profile, service })

  let payload: unknown
  try {
    const response = await fetch(ROUTE_ENDPOINT, {
      method: 'POST',
      // Explicite : c'est ce type que PHP sait décomposer en `$_POST`.
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: form,
      signal,
    })
    if (!response.ok) return null
    payload = await response.json()
  } catch (error) {
    // Une annulation est une décision de l'appelant, pas une panne : on la
    // laisse remonter pour qu'il ne la confonde pas avec un échec de routage.
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  }

  if (typeof payload !== 'object' || payload === null) return null
  const body = payload as Record<string, unknown>
  if (typeof body.geometry !== 'string') return null

  const points = decodePolyline6Validated(body.geometry)
  if (points === null || points.length < 2) return null

  return {
    points,
    distance: typeof body.distance === 'number' ? body.distance : 0,
    duration: typeof body.duration === 'number' ? body.duration : 0,
  }
}
